import React from "react";
import { Link, useLocation } from "react-router-dom";
import { featureManifest, NAV_CATEGORIES, BY_ROLE_PAGES } from "./featureManifest";
import "./marketing.css";

function GroupedMegaMenu({ onNavClick }) {
  return (
    <div className="absolute left-1/2 top-full z-50 hidden -translate-x-1/2 pt-2 group-hover:block">
      <div
        className="w-[min(1120px,90vw)] rounded-xl border border-[var(--mk-border-subtle)] bg-[var(--mk-glass)] p-6 shadow-2xl backdrop-blur-2xl"
        style={{ WebkitBackdropFilter: "blur(24px)" }}
        data-testid="mk-mega-menu"
      >
        <div className="grid grid-cols-5 gap-6">
          {NAV_CATEGORIES.map((cat) => {
            const items = featureManifest.filter((f) => f.category === cat.id);
            return (
              <div key={cat.id}>
                <p className="mk-mono mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-accent)]">
                  {cat.id}
                </p>
                <p className="mb-4 text-xs text-[var(--mk-text-muted)]">{cat.blurb}</p>
                <ul className="space-y-2">
                  {items.map((f) => (
                    <li key={f.slug}>
                      <Link
                        to={`/product/${f.slug}`}
                        onClick={onNavClick}
                        className="mk-link block text-sm font-medium transition-colors"
                        data-testid={`mk-menu-${f.slug}`}
                      >
                        {f.nav}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RoleMenu({ onNavClick }) {
  return (
    <div className="absolute left-1/2 top-full z-50 hidden -translate-x-1/2 pt-2 group-hover:block">
      <div
        className="w-[min(520px,90vw)] rounded-xl border border-[var(--mk-border-subtle)] bg-[var(--mk-glass)] p-5 shadow-2xl backdrop-blur-2xl"
        style={{ WebkitBackdropFilter: "blur(24px)" }}
      >
        <p className="mk-mono mb-4 text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-accent)]">
          By Role
        </p>
        <ul className="space-y-3">
          {BY_ROLE_PAGES.map((r) => (
            <li key={r.slug}>
              <Link
                to={`/product/${r.slug}`}
                onClick={onNavClick}
                className="mk-link block text-sm font-semibold"
                data-testid={`mk-menu-${r.slug}`}
              >
                {r.nav}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TopNav() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const closeMobile = () => setMobileOpen(false);
  return (
    <header
      className="sticky top-0 z-40 border-b border-[var(--mk-border-subtle)]"
      style={{ backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)", backgroundColor: "rgba(20,20,20,0.72)" }}
      data-testid="mk-topnav"
    >
      <div className="mk-container flex h-16 items-center justify-between">
        <Link to="/product" onClick={closeMobile} className="flex items-center gap-2" data-testid="mk-nav-logo">
          <img
            src="/fixlr-logo-light.png"
            alt="Fixlr"
            className="h-8 w-auto select-none"
            draggable="false"
          />
          <span className="mk-badge !py-0.5 hidden md:inline-flex">Platform</span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          <div className="group relative">
            <button className="mk-btn mk-btn-ghost !px-3 !py-2" data-testid="mk-nav-platform">
              Platform ▾
            </button>
            <GroupedMegaMenu onNavClick={closeMobile} />
          </div>
          <div className="group relative">
            <button className="mk-btn mk-btn-ghost !px-3 !py-2" data-testid="mk-nav-solutions">
              Solutions ▾
            </button>
            <RoleMenu onNavClick={closeMobile} />
          </div>
          <Link to="/product/pricing" className="mk-btn mk-btn-ghost !px-3 !py-2" data-testid="mk-nav-pricing">
            Pricing
          </Link>
          <Link to="/product/about" className="mk-btn mk-btn-ghost !px-3 !py-2" data-testid="mk-nav-about">
            About
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/login" className="mk-btn mk-btn-ghost hidden md:inline-flex" data-testid="mk-nav-signin">
            Sign in
          </Link>
          <Link to="/product/demo" className="mk-btn mk-btn-secondary hidden md:inline-flex" data-testid="mk-nav-demo">
            Book a demo
          </Link>
          <Link to="/signup" className="mk-btn mk-btn-primary" data-testid="mk-nav-signup">
            Start free
          </Link>
          <button
            className="md:hidden mk-btn mk-btn-ghost !p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mk-nav-mobile-toggle"
          >
            <span className="inline-block h-4 w-5 relative">
              <span className="absolute left-0 right-0 top-0 h-[2px] bg-current" />
              <span className="absolute left-0 right-0 top-1/2 h-[2px] -translate-y-1/2 bg-current" />
              <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-current" />
            </span>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-[var(--mk-border-subtle)] bg-[var(--mk-bg)]" data-testid="mk-mobile-menu">
          <div className="mk-container py-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {NAV_CATEGORIES.map((cat) => {
              const items = featureManifest.filter((f) => f.category === cat.id);
              return (
                <div key={cat.id}>
                  <p className="mk-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-accent)] mb-2">
                    {cat.id}
                  </p>
                  <ul className="space-y-1.5 pl-2">
                    {items.map((f) => (
                      <li key={f.slug}>
                        <Link to={`/product/${f.slug}`} onClick={closeMobile} className="mk-link text-sm">
                          {f.nav}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            <div className="flex gap-2 pt-2 border-t border-[var(--mk-border-subtle)]">
              <Link to="/product/pricing" onClick={closeMobile} className="mk-btn mk-btn-secondary flex-1">Pricing</Link>
              <Link to="/product/demo" onClick={closeMobile} className="mk-btn mk-btn-primary flex-1">Demo</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[var(--mk-border-subtle)] bg-[var(--mk-bg)] relative mk-grain" data-testid="mk-footer">
      <div className="mk-container py-16 relative z-10">
        <div className="grid gap-10 md:grid-cols-5">
          <div className="md:col-span-2 space-y-3">
            <img
              src="/fixlr-logo-light.png"
              alt="Fixlr"
              className="h-8 w-auto select-none"
              draggable="false"
            />
            <p className="max-w-sm text-sm text-[var(--mk-text-dim)]">
              The multi-tenant maintenance management platform built for teams that run on minutes, not days.
            </p>
            <div className="flex gap-2 pt-2">
              <Link to="/signup" className="mk-btn mk-btn-primary text-xs">Start free</Link>
              <Link to="/product/demo" className="mk-btn mk-btn-secondary text-xs">Book a demo</Link>
            </div>
          </div>

          <div>
            <p className="mk-mono mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-accent)]">
              Platform
            </p>
            <ul className="space-y-2 text-sm text-[var(--mk-text-dim)]">
              {featureManifest.slice(0, 8).map((f) => (
                <li key={f.slug}>
                  <Link to={`/product/${f.slug}`} className="mk-link">{f.nav}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mk-mono mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-accent)]">
              More
            </p>
            <ul className="space-y-2 text-sm text-[var(--mk-text-dim)]">
              {featureManifest.slice(8).map((f) => (
                <li key={f.slug}>
                  <Link to={`/product/${f.slug}`} className="mk-link">{f.nav}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mk-mono mb-3 text-[10px] font-bold uppercase tracking-[0.25em] text-[var(--mk-accent)]">
              Company
            </p>
            <ul className="space-y-2 text-sm text-[var(--mk-text-dim)]">
              <li><Link to="/product/about" className="mk-link">About</Link></li>
              <li><Link to="/product/pricing" className="mk-link">Pricing</Link></li>
              <li><Link to="/product/demo" className="mk-link">Book a demo</Link></li>
              <li><Link to="/login" className="mk-link">Sign in</Link></li>
              <li><Link to="/signup" className="mk-link">Start free</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-[var(--mk-border-subtle)] pt-6 text-xs text-[var(--mk-text-muted)] md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Fixlr. Maintenance management, properly engineered.</p>
          <p className="mk-mono">fixlr.app</p>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingLayout({ children }) {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="mk-root" data-testid="mk-root">
      <TopNav />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
